/**
 * Stale-bundle defence.
 *
 * Why this exists: iOS Safari and some CDNs ignore `Cache-Control:
 * must-revalidate` on the HTML document, leaving returning users on an
 * outdated bundle while the server has shipped a fix. The user sees
 * mysterious failures that vanish in incognito or after a full cache wipe.
 *
 * How it works:
 *  - Build time: Vite embeds a unique `__APP_VERSION__` constant into the
 *    bundle and writes `/version.json` with the same value.
 *  - Boot: we fetch `/version.json` bypassing every cache. If the value
 *    differs from the embedded constant, the page is running stale code.
 *    We clear the CacheStorage entries we own and trigger a hard reload.
 *  - Background: we re-check on tab focus (handles iOS resume-from-bg)
 *    and every 10 minutes for long-lived sessions.
 *
 * Auth/local state is intentionally preserved across the reload — losing
 * the session for a routine bundle refresh would be far worse UX than the
 * stale bundle itself.
 */

declare const __APP_VERSION__: string;

const VERSION_URL = "/version.json";
const RECHECK_MS = 10 * 60 * 1000;
const RELOAD_FLAG = "arkeonx_version_reloaded_at";
// Avoid reload loops: if we already reloaded within this window, hold off.
const RELOAD_COOLDOWN_MS = 30 * 1000;

const CACHE_PREFIXES_TO_DROP = ["workbox-", "arkeonx-"];

const clearCachesQuietly = async () => {
  if (typeof window === "undefined" || !("caches" in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((k) => CACHE_PREFIXES_TO_DROP.some((p) => k.startsWith(p)))
        .map((k) => caches.delete(k).catch(() => false))
    );
  } catch {
    // ignore — best effort
  }
};

const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null;
  }
};

const reloadOnce = async () => {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
    if (lastReload && Date.now() - lastReload < RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable — still safe to reload once
  }
  await clearCachesQuietly();
  window.location.reload();
};

const runCheck = async () => {
  const current = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "";
  if (!current) return;
  const latest = await fetchLatestVersion();
  if (!latest) return;
  if (latest !== current) {
    console.warn("[version] stale bundle detected, reloading", { current, latest });
    await reloadOnce();
  }
};

/**
 * Start the version-check loop. Safe to call multiple times — it tracks
 * its own interval/listeners and self-deduplicates.
 */
let started = false;
export const startVersionCheck = () => {
  if (started) return;
  started = true;
  if (typeof window === "undefined") return;

  // Initial check (deferred so it doesn't compete with first paint)
  setTimeout(runCheck, 3000);

  // Periodic background check
  setInterval(runCheck, RECHECK_MS);

  // Tab focus / iOS resume-from-bg
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") runCheck();
  });
};

/**
 * Nuclear reset for users that have somehow ended up in a wedged state
 * (zombie session, stale SW, corrupted localStorage). Drops all Arkeonx
 * and Supabase storage, unregisters service workers, clears caches, and
 * hard-reloads. Intended to be wired to a visible "Reset app" button.
 */
export const resetAppHard = async () => {
  if (typeof window === "undefined") return;
  try {
    const { clearAllAuthStorage } = await import("@/lib/storage/cleanup");
    clearAllAuthStorage();
  } catch {
    // best effort
  }
  await clearCachesQuietly();
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    // best effort
  }
  // Bypass HTTP cache on reload where supported (Firefox/Safari; ignored in Chrome).
  window.location.reload();
};
